import { Outlet, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import OwnerDecodedSidebar from "../components/OwnerDecodedSidebar";
import NotificationBell from "../components/NotificationBell";
import ProfileMenu from "../components/ProfileMenu";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useAuth } from "../context/AuthContext";
import { fetchMyVehicles } from "../api/vehicles";
import useFleetHealth from "../hooks/useFleetHealth";
import API_URL from "../api/baseURL";

export default function OwnerLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const { token, user, logout } = useAuth();
  const { pathname } = useLocation();

  // Pages converted to the chrome/ink design own their max-width and padding
  // (via components/ui/Iparc <Page>), so they render full-bleed. Anything not
  // yet converted keeps the original centred container. Add a route here when
  // its page is converted.
  const FULL_BLEED = [
    "/dashboard",
    "/vehicles",
    "/owner/quotations",
    "/owner/invoices",
    "/book-workshop",
    "/dashboard/invoices",
    "/add-vehicle",
  ];
  // The invoice detail route carries an id, so it can never be in the list.
  const isFullBleed = FULL_BLEED.includes(pathname) || pathname.startsWith("/owner/invoices/");

  // Same query key as the dashboard, so react-query serves it from cache
  // instead of issuing a second request just to label the sidebar.
  const { data: vehicles = [] } = useQuery({
    queryKey: ["myVehicles"],
    queryFn: fetchMyVehicles,
    enabled: !!token,
  });
  const { issueCount } = useFleetHealth(vehicles);

  // Notifications polling
  useEffect(() => {
    if (!token) return;
    const fetchNotifs = () =>
      axios
        .get(`${API_URL}/notifications`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setNotifications(res.data))
        .catch(() => {});
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 15000);
    return () => clearInterval(interval);
  }, [token]);

  return (
    <div className="min-h-screen flex bg-[#0D0C0B]">
      {/* Desktop Sidebar */}
      <div className="hidden sm:block">
        <OwnerDecodedSidebar
          vehicleCount={vehicles.length}
          issueCount={issueCount}
          onLogout={logout}
        />
      </div>

      {/* Mobile Sidebar (slide-over) */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 flex sm:hidden">
          <div className="relative w-64 bg-gray-900 text-white">
            <button
              className="absolute top-2 right-2 p-2 text-white"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              ✖
            </button>
            <OwnerDecodedSidebar
              vehicleCount={vehicles.length}
              issueCount={issueCount}
              onLogout={logout}
            />
          </div>
          {/* Backdrop */}
          <div
            className="flex-1 bg-black bg-opacity-40"
            onClick={() => setSidebarOpen(false)}
          />
        </div>
      )}

      {/* Main Content */}
      <main
        className={`flex-1 pt-6 pb-2 overflow-x-auto text-white bg-[#0D0C0B] ${
          isFullBleed ? "px-0" : "px-2 sm:px-4"
        }`}
      >
        {/* Top bar: mobile menu + right-side icons */}
        {/* Top bar keeps its own gutter when main goes edge-to-edge (dashboard) */}
        <div className={`flex items-center justify-between mb-4 ${isFullBleed ? "px-[30px]" : ""}`}>
          {/* Mobile hamburger */}
          <div className="sm:hidden flex items-center">
            <button
              className="p-2 rounded bg-gray-800 text-white"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <span className="ml-3 text-lg font-bold">Dashboard</span>
          </div>
          {/* Spacer for desktop (hamburger hidden) */}
          <div className="hidden sm:block" />
          {/* Profile, notifications, language — always visible */}
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <NotificationBell
              notifications={notifications}
              onRefresh={() =>
                axios
                  .get(`${API_URL}/notifications`, { headers: { Authorization: `Bearer ${token}` } })
                  .then((res) => setNotifications(res.data))
                  .catch(() => {})
              }
            />
            <ProfileMenu user={user} />
          </div>
        </div>
        <div className={isFullBleed ? "" : "max-w-screen-xl mx-auto"}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
